/**
 * Printable plan report (spec §19): generated client-side via the browser's
 * print-to-PDF — inputs, key charts, the year-by-year table, and the
 * assumptions used, with the tax-constants date stamped. Rendered under the
 * active assumption profile (switch profiles in the header, then print again
 * for the optimistic / pessimistic versions).
 */
import { useEffect } from 'react';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend } from 'recharts';
import type { Plan } from '../model/types';
import type { YearSnapshot } from '../engine/snapshot';
import { ACCOUNT_TYPE_LABELS } from '../model/accountMeta';
import { netWorthToday } from '../model/netWorth';
import { CONSTANTS_AS_OF } from '../engine/data/federal';
import { stateProfileFor } from '../engine/data/states';
import { fmtCompactUsd, fmtUsd } from './format';

interface Props {
  plan: Plan;
  snapshots: YearSnapshot[];
  todaysDollars: boolean;
  onClose: () => void;
}

const pctS = (v: number) => `${(v * 100).toFixed(1)}%`;

export function ReportView({ plan, snapshots, todaysDollars, onClose }: Props) {
  // Force the light theme for print, restore afterwards, and auto-open the
  // print dialog once the charts have painted.
  useEffect(() => {
    const prev = document.documentElement.dataset.theme;
    document.documentElement.dataset.theme = 'light';
    const t = setTimeout(() => window.print(), 350);
    const done = () => onClose();
    window.addEventListener('afterprint', done);
    return () => {
      clearTimeout(t);
      window.removeEventListener('afterprint', done);
      if (prev) document.documentElement.dataset.theme = prev;
      else delete document.documentElement.dataset.theme;
    };
  }, [onClose]);

  const last = snapshots[snapshots.length - 1]!;
  const d = (s: YearSnapshot, v: number) => (todaysDollars ? v / s.cumulativeInflationFactor : v);
  const person = plan.household.people[0]!;
  const retirementYear = person.birthYear + person.retirementAge;
  const atRet = snapshots.find((s) => s.year === retirementYear);
  const lifetimeTax = snapshots.reduce((t, s) => t + s.totalTaxLiability, 0);
  const active = plan.assumptionProfiles.find((p) => p.label === plan.activeAssumptionLabel)!;
  const stateProfile = stateProfileFor(plan.household.stateOfResidence);

  const familyData = snapshots.map((s) => ({
    year: s.year,
    'Pre-tax': Math.round(d(s, s.balancesByFamily.pretax)),
    Roth: Math.round(d(s, s.balancesByFamily.roth)),
    'After-tax': Math.round(d(s, s.balancesByFamily.aftertax)),
  }));
  const guaranteedData = snapshots.map((s) => ({
    year: s.year,
    'Guaranteed income': Math.round(d(s, s.guaranteedIncome)),
    'Must-spend': Math.round(
      d(s, s.essentialSpending + s.healthcarePremiums + s.healthcareOOP + s.ltcNetCost),
    ),
  }));

  return (
    <div className="report-root">
      <div className="report-bar no-print">
        <span>Print dialog opening — choose "Save as PDF" as the destination.</span>
        <button className="btn" onClick={() => window.print()}>Print again</button>
        <button className="btn" onClick={onClose}>Back to the planner</button>
      </div>

      <h1>Retirement plan report</h1>
      <p className="report-meta">
        Generated {new Date().toLocaleDateString('en-US')} · assumption profile:{' '}
        <strong>{plan.activeAssumptionLabel}</strong> · dollars shown:{' '}
        {todaysDollars ? "today's" : 'future (nominal)'} · tax constants as of {CONSTANTS_AS_OF}
      </p>
      <p className="report-disclaimer">
        An educational model built on the assumptions below — not financial, tax, or legal
        advice. Tax law changes; constants may be out of date. Consequential decisions warrant a
        professional with a fiduciary duty.
      </p>

      <h2>The headline numbers</h2>
      <table className="report-table">
        <tbody>
          <tr><td>Net worth today</td><td>{fmtUsd(Math.round(netWorthToday(plan)))}</td></tr>
          {atRet && (
            <tr>
              <td>At retirement ({retirementYear}, age {person.retirementAge})</td>
              <td>{fmtUsd(Math.round(d(atRet, atRet.netWorth)))}</td>
            </tr>
          )}
          <tr>
            <td>At the end of the plan ({last.year})</td>
            <td>{fmtUsd(Math.round(d(last, last.netWorth)))}</td>
          </tr>
          <tr><td>Lifetime taxes (nominal)</td><td>{fmtUsd(Math.round(lifetimeTax))}</td></tr>
        </tbody>
      </table>

      <h2>Household</h2>
      <table className="report-table">
        <tbody>
          {plan.household.people.map((p) => (
            <tr key={p.id}>
              <td>{p.label}</td>
              <td>
                born {p.birthYear} · retires at {p.retirementAge} · claims Social Security at{' '}
                {plan.decisions.claimingAgeOverride?.[p.id] ?? p.socialSecurity.claimingAge} · plan
                to {p.lifeExpectancyAge}
              </td>
            </tr>
          ))}
          <tr>
            <td>Filing / state</td>
            <td>
              {plan.household.filingStatus.toUpperCase()} · {stateProfile.name} (
              {stateProfile.fidelity === 'none' ? 'no income tax'
                : stateProfile.fidelity === 'modeled' ? 'modeled in detail'
                : 'flat approximation'}
              , verified {stateProfile.lastVerified})
            </td>
          </tr>
        </tbody>
      </table>

      <h2>Accounts</h2>
      <table className="report-table">
        <thead><tr><th>Account</th><th>Type</th><th>Balance</th><th>As of</th></tr></thead>
        <tbody>
          {plan.accounts.map((a) => (
            <tr key={a.id}>
              <td>{a.label}</td>
              <td>{ACCOUNT_TYPE_LABELS[a.type]}</td>
              <td>{fmtUsd(a.balance)}</td>
              <td>{a.asOfDate ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {plan.incomes.length > 0 && (
        <>
          <h2>Income</h2>
          <table className="report-table">
            <tbody>
              {plan.incomes.map((inc) => (
                <tr key={inc.id}>
                  <td>{inc.label}</td>
                  <td>{fmtUsd(inc.annualAmount)}/yr · ages {inc.startAge}–{inc.endAge}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      <h2>Assumptions in effect ({active.label})</h2>
      <table className="report-table">
        <tbody>
          <tr>
            <td>Returns</td>
            <td>
              stocks {pctS(active.returnsByAssetClass.equity)} · bonds{' '}
              {pctS(active.returnsByAssetClass.bond)} · cash {pctS(active.returnsByAssetClass.cash)}
            </td>
          </tr>
          <tr>
            <td>Inflation</td>
            <td>
              general {pctS(active.generalInflation)} · healthcare {pctS(active.healthcareInflation)} ·
              long-term care {pctS(active.ltcInflation)}
            </td>
          </tr>
          <tr><td>Wage growth / home appreciation</td>
            <td>{pctS(active.wageGrowth)} / {pctS(active.housingAppreciation)}</td></tr>
          <tr>
            <td>Social Security</td>
            <td>
              {active.socialSecurityHaircut
                ? `policy assumption: ${Math.round(active.socialSecurityHaircut.payableFraction * 100)}% payable from ${active.socialSecurityHaircut.startYear}`
                : 'full scheduled benefits'}
            </td>
          </tr>
          <tr>
            <td>Strategy</td>
            <td>
              {plan.decisions.withdrawalStrategy === 'fixed_percentage'
                ? `fixed ${((plan.decisions.fixedPercentage ?? 0.04) * 100).toFixed(1)}% withdrawals`
                : 'fund spending needs'} ·{' '}
              {plan.decisions.orderMode === 'bracket_aware'
                ? `bracket-aware to the ${((plan.decisions.bracketAwareCeiling ?? 0.22) * 100).toFixed(0)}% bracket`
                : plan.decisions.orderMode === 'custom' ? 'custom withdrawal order' : 'traditional withdrawal order'}
              {plan.decisions.rothConversionRule &&
                ` · Roth conversions ${plan.decisions.rothConversionRule.startYear}–${plan.decisions.rothConversionRule.endYear}`}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="report-break">Account balances by tax type</h2>
      <AreaChart width={680} height={230} data={familyData}>
        <CartesianGrid stroke="#ddd" vertical={false} />
        <XAxis dataKey="year" tick={{ fontSize: 11 }} minTickGap={28} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => fmtCompactUsd(v)} width={58} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Area dataKey="Pre-tax" stackId="a" fill="#4a7db5" stroke="#4a7db5" fillOpacity={0.6} />
        <Area dataKey="Roth" stackId="a" fill="#3d8f5f" stroke="#3d8f5f" fillOpacity={0.6} />
        <Area dataKey="After-tax" stackId="a" fill="#c2733a" stroke="#c2733a" fillOpacity={0.6} />
      </AreaChart>

      <h2>Guaranteed income vs. must-spend</h2>
      <LineChart width={680} height={220} data={guaranteedData}>
        <CartesianGrid stroke="#ddd" vertical={false} />
        <XAxis dataKey="year" tick={{ fontSize: 11 }} minTickGap={28} />
        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => fmtCompactUsd(v)} width={58} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Line dataKey="Guaranteed income" stroke="#4a7db5" strokeWidth={2} dot={false} />
        <Line dataKey="Must-spend" stroke="#c2733a" strokeWidth={2} dot={false} />
      </LineChart>

      <h2 className="report-break">Year by year</h2>
      <table className="report-table report-years">
        <thead>
          <tr>
            <th>Year</th><th>Age</th><th>Income</th><th>Soc. Sec.</th><th>Spending</th>
            <th>Withdrawals</th><th>Taxes</th><th>Net worth</th>
          </tr>
        </thead>
        <tbody>
          {snapshots.map((s) => (
            <tr key={s.year}>
              <td>{s.year}</td>
              <td>{s.ages[person.id]}</td>
              <td>{fmtCompactUsd(d(s, s.wagesIncome + s.selfEmploymentIncome + s.pensionIncome + s.annuityIncome + s.rentalRoyaltyOtherIncome))}</td>
              <td>{fmtCompactUsd(d(s, s.socialSecurityGross))}</td>
              <td>{fmtCompactUsd(d(s, s.totalSpending + s.healthcarePremiums + s.healthcareOOP + s.ltcNetCost))}</td>
              <td>{fmtCompactUsd(d(s, s.totalWithdrawals + s.totalRmd))}</td>
              <td>{fmtCompactUsd(d(s, s.totalTaxLiability))}</td>
              <td>{fmtCompactUsd(d(s, s.netWorth))}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="report-meta">
        Generated locally in the browser — no data left this device. Tax constants as of{' '}
        {CONSTANTS_AS_OF}.
      </p>
    </div>
  );
}
